import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Alert, Chip, SxProps, Theme } from "@mui/material";
import CloserTooltip from "./CloserTooltip";
import moment from "moment";
import EventResetsService, { TripEventResetStatus } from "services/eventResetsService";
import { eventResetsLink } from "reviewQueue/eventResets";
import { responseErrorText } from "reviewQueue/bulkEventReset";

// While a bulk Event Reset holds the trip, re-check this often so the banner clears by itself.
export const QUEUED_RECHECK_MS = 15000;

type StatusLoader = (tripId: number) => Promise<TripEventResetStatus>;

export const isQueuedForReset = (s?: TripEventResetStatus | null) => !!s?.state;

const fmt = (iso?: string | null) => (iso ? moment(iso).format("MM/DD/YYYY HH:mm") : "");

export const queuedLabel = (s: TripEventResetStatus) =>
  `${s.state === "Running" ? "Event Reset running" : "Queued for Event Reset"}${s.queuedBy ? ` by ${s.queuedBy}` : ""}${
    s.queuedAt ? ` at ${fmt(s.queuedAt)}` : ""
  }`;

export const queuedMessage = (s: TripEventResetStatus) => `${queuedLabel(s)} — editing is disabled until it finishes.`;

export const lastResetLabel = (s: TripEventResetStatus) =>
  `Events reset ${fmt(s.lastResetAt)}${s.lastResetBy ? ` by ${s.lastResetBy}` : ""}`;

// The auto-approver's verdict from the last bulk run, when this trip's last reset was a bulk one.
export const lastApprovalText = (s: TripEventResetStatus): string | null => {
  if (!s.lastResetBatchId || s.lastBatchId !== s.lastResetBatchId) return null;
  if (s.lastAutoApproved === true) return "Auto-approved";
  if (s.lastAutoApproved === false) return `Auto-approve failed${s.lastAutoApproveFailReason ? `: ${s.lastAutoApproveFailReason}` : ""}`;
  return s.lastBatchTripStatus === "Failed" ? "Not sent to auto-approve" : null;
};

// Green when the last bulk run auto-approved the trip, amber when auto-approve failed, plain otherwise.
export const lastResetColor = (s: TripEventResetStatus): "success" | "warning" | "default" => {
  if (!s.lastResetBatchId || s.lastBatchId !== s.lastResetBatchId) return "default";
  if (s.lastAutoApproved === true) return "success";
  return s.lastAutoApproved === false ? "warning" : "default";
};

// A 409 from an event write: the trip is queued for a bulk Event Reset. Returns OrderApi's message.
export const conflictMessage = (error: any): string | null =>
  error?.response?.status === 409 ? responseErrorText(error) || "This trip is queued for an Event Reset and can't be edited right now." : null;

// A bulk reset finished on this trip since the last check: it was queued or running and no longer is,
// or the trip carries a bulk reset stamp it did not have before. Its events were replaced underneath
// whatever is on screen. A closer's own single-trip reset carries no batch id, so it never counts.
export const resetFinished = (prev: TripEventResetStatus | null, next: TripEventResetStatus) =>
  !!prev &&
  ((isQueuedForReset(prev) && !isQueuedForReset(next)) ||
    (!!next.lastResetBatchId && (next.lastResetBatchId !== prev.lastResetBatchId || next.lastResetAt !== prev.lastResetAt)));

/**
 * A trip's Event Reset status: loaded with the trip, re-checked when the window regains focus,
 * and re-checked every 15 s while a bulk reset holds it. refresh() re-checks on demand (after a 409).
 * When a bulk reset finishes while the trip is open, onResetFinished reloads the trip's events; the
 * trip stays read-only (queued) until that reload is done so stale event ids can't be saved over it.
 * Answers for a trip that is no longer open, or superseded by a newer check, are dropped.
 */
export const useTripResetStatus = (
  tripId?: number,
  load: StatusLoader = EventResetsService.getTripStatus,
  onResetFinished?: () => Promise<unknown> | void
) => {
  const [status, setStatus] = useState<TripEventResetStatus | null>(null);
  const [reloading, setReloading] = useState(false);
  // Updated at render so a late answer for the previous trip is dropped before the effects run.
  const tripIdRef = useRef(tripId);
  tripIdRef.current = tripId;
  const statusRef = useRef<TripEventResetStatus | null>(null);
  const seqRef = useRef(0);
  const onResetFinishedRef = useRef(onResetFinished);
  onResetFinishedRef.current = onResetFinished;

  const refresh = useCallback(async () => {
    if (!tripId) return;
    const seq = ++seqRef.current;
    const current = () => tripIdRef.current === tripId && seqRef.current === seq;
    let next: TripEventResetStatus;
    try {
      next = await load(tripId);
    } catch {
      // Status is advisory: OrderApi still refuses edits on a queued trip.
      return;
    }
    if (!current()) return;
    const finished = resetFinished(statusRef.current, next);
    statusRef.current = next;
    if (finished && onResetFinishedRef.current) {
      setReloading(true);
      try {
        await onResetFinishedRef.current();
      } catch {
        // The reload reports its own failure; still let the closer go.
      }
      if (tripIdRef.current !== tripId) return;
      setReloading(false);
      // A newer check landed while the events reloaded and already set the status.
      if (seqRef.current !== seq) return;
    }
    setStatus(next);
  }, [tripId, load]);

  useEffect(() => {
    statusRef.current = null;
    setStatus(null);
    setReloading(false);
    refresh();
  }, [tripId, refresh]);

  useEffect(() => {
    window.addEventListener("focus", refresh);
    return () => window.removeEventListener("focus", refresh);
  }, [refresh]);

  const held = isQueuedForReset(status);
  useEffect(() => {
    if (!held) return;
    const timer = setInterval(refresh, QUEUED_RECHECK_MS);
    return () => clearInterval(timer);
  }, [held, refresh]);

  return { status, queued: held || reloading, refresh };
};

/** Yellow banner on the closer while a bulk reset holds the trip. */
export const TripResetQueuedBanner: React.FC<{ status: TripEventResetStatus | null }> = ({ status }) =>
  status && isQueuedForReset(status) ? (
    <Alert severity="warning" sx={{ mb: 1 }} data-testid="trip-reset-queued">
      {queuedMessage(status)}
    </Alert>
  ) : null;

export const hasResetChip = (s?: TripEventResetStatus | null) => !!s && (isQueuedForReset(s) || !!s.lastResetAt);

/**
 * "Reset MM/DD/YYYY" chip: when the trip's events were last reset, coloured by the auto-approver's
 * verdict, with who and the verdict on hover. Admins click through to the run.
 */
export const TripResetChip: React.FC<{
  status: TripEventResetStatus | null;
  canOpenHistory: boolean;
  size?: "small" | "medium";
  sx?: SxProps<Theme>;
}> = ({ status, canOpenHistory, size, sx }) => {
  if (!status || !hasResetChip(status)) return null;
  if (isQueuedForReset(status)) {
    return (
      <CloserTooltip title={queuedLabel(status)}>
        <Chip label="Reset queued" color="warning" size={size} sx={sx} data-testid="trip-reset-chip" />
      </CloserTooltip>
    );
  }
  const approval = lastApprovalText(status);
  const link = canOpenHistory && status.lastResetBatchId ? eventResetsLink(status.lastResetBatchId) : null;
  const title = (
    <>
      <div>{lastResetLabel(status)}</div>
      {approval && <div>{approval}</div>}
      {link && <div>Click to open the run</div>}
    </>
  );
  const label = `Reset ${moment(status.lastResetAt).format("MM/DD/YYYY")}`;
  return (
    <CloserTooltip title={title}>
      {link ? (
        <Chip component={Link} to={link} clickable label={label} color={lastResetColor(status)} size={size} sx={sx} data-testid="trip-reset-chip" />
      ) : (
        <Chip label={label} color={lastResetColor(status)} size={size} sx={sx} data-testid="trip-reset-chip" />
      )}
    </CloserTooltip>
  );
};
